import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Skeleton,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductForm from '../../components/admin/ProductForm';
import { useImageBlob } from '../../hooks/useImageBlob';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products`
      );
      setProducts(response.data);
      setError('');
    } catch (err) {
      setError(t('admin.products.errorFetching'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleOpen = (product = null) => {
    setSelectedProduct(product);
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedProduct(null);
    setOpen(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('Confirm Delete'))) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/products/${id}`);
        fetchProducts();
      } catch (err) {
        setError(t('Error deleting product'));
      }
    }
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedProduct) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/products/${selectedProduct?._id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/products`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      }
      handleClose();
      fetchProducts();
    } catch (err) {
      setError(
        selectedProduct
          ? t('Error updating product')
          : t('Error creating product')
      );
    }
  };

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <Typography>{t('Loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Typography variant='h4'>{t('Products Management')}</Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          {t('Add Product')}
        </Button>
      </Box>

      {error && (
        <Typography color='error' mb={2}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3}>
        {products?.map?.((product) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product?._id}>
            <ProductCard
              product={product}
              onEdit={() => handleOpen(product)}
              onDelete={() => handleDelete(product?._id)}
            />
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <DialogTitle>
          {selectedProduct ? t('Edit Product') : t('Add Product')}
        </DialogTitle>
        <DialogContent>
          <ProductForm
            product={selectedProduct}
            onSubmit={handleSubmit}
            onCancel={handleClose}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// Separate component for product card to manage individual image blobs
const ProductCard = ({ product, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Use the custom hook to convert image path to blob URL
  const { blobUrl, loading: imageLoading } = useImageBlob(
    product?.mainImage,
    apiUrl
  );

  return (
    <Card>
      <CardContent>
        {imageLoading ? (
          <Skeleton
            variant='rectangular'
            width='100%'
            height={200}
            sx={{ mb: 2, borderRadius: 1 }}
          />
        ) : (
          <Box
            component='img'
            src={blobUrl || ''}
            alt={product?.name}
            sx={{
              width: '100%',
              height: 200,
              objectFit: 'cover',
              mb: 2,
              borderRadius: 1,
            }}
          />
        )}
        <Typography variant='h6' gutterBottom>
          {product?.name}
        </Typography>
        <Typography color='textSecondary' gutterBottom>
          {product?.category}
        </Typography>
        <Box display='flex' justifyContent='flex-end' mt={2}>
          <IconButton color='primary' onClick={onEdit} size='small'>
            <EditIcon />
          </IconButton>
          <IconButton color='error' onClick={onDelete} size='small'>
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AdminProducts;
